'use client';

import { motion } from 'motion/react';
import { PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { useHydrationSafeResponsive } from '~/lib/hooks/useResponsive';
import useVelocityMarquee from '~/lib/hooks/useVelocityMarquee';
import { cn } from '~/lib/utils/classname';

export interface Props extends PropsWithChildren {
  className?: string;
  wrapperClassName?: string;
  gap?: number;
  mobileGap?: number;
  repeat?: number;
  baseVelocity?: number;
  mobileVelocity?: number;
}

const Marquee: React.FC<Props> = ({
  className,
  wrapperClassName,
  gap: desktopGap = 0,
  mobileGap = desktopGap,
  repeat = 3,
  baseVelocity: desktopVelocity = 10,
  mobileVelocity = desktopVelocity,
  children,
}) => {
  const [gap, setGap] = useState<number>(desktopGap);
  const [baseVelocity, setBaseVelocity] = useState<number>(desktopVelocity);

  const repeater = useMemo(
    () => Array.from({ length: repeat }).map((_, i) => i),
    [repeat]
  );

  const isMobile = useHydrationSafeResponsive('mobile');
  const reducedMotion = useHydrationSafeResponsive('prefers-reduced-motion');

  useEffect(() => {
    if (isMobile) {
      setGap(mobileGap);
      setBaseVelocity(mobileVelocity);
    } else {
      setGap(desktopGap);
      setBaseVelocity(desktopVelocity);
    }
  }, [desktopGap, desktopVelocity, isMobile, mobileGap, mobileVelocity]);

  const x = useVelocityMarquee({
    baseVelocity,
    mapVelocityFactor: [0, 0.75],

    mapProgress(progress) {
      return `calc(-${progress + 100}% - ${gap + gap * (progress / 100)}px)`;
    },
  });

  return (
    <div className={cn('relative flex w-full overflow-hidden', className)}>
      <motion.div
        className={cn('flex flex-row flex-nowrap')}
        style={{ gap: `${gap}px` }}
      >
        {repeater.map((index) => (
          <motion.ul
            key={index}
            className={cn(
              'flex transform-gpu flex-row flex-nowrap',
              wrapperClassName
            )}
            style={{ gap: `${gap}px`, x: reducedMotion ? 0 : x }}
          >
            {children}
          </motion.ul>
        ))}
      </motion.div>
    </div>
  );
};

export default Marquee;
